import React, { useState } from "react";
import { Share2, Copy, Mail, Link2, X, Linkedin, MessageCircle } from "lucide-react";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from 'uuid';

import {generateShareLink, inviteMailShare} from '../api/api.js';

export default function ShareModal({ isOpen, onClose, threadId, searchId }) {
  const [activeTab, setActiveTab] = useState("email");
  const [shareLink, setShareLink] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const token = Cookies.get("authToken");

  const handleGenerateLink = async () => {
    setIsLoading(true);
    try {
      const res = await generateShareLink(token, threadId, searchId, uuidv4());
      //const data = await res.json();

      if(res.responsecode == 200){
        setShareLink(res.shareLink);
      }
      else{
        setErrorMessage(" Failed to generate link");
      }

    } catch (err) {
      setErrorMessage(" Failed to generate link");
    }
    setIsLoading(false);
  };

  const handleSendEmail = async () => {

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setIsLoading(true);
    try {
      const res = await inviteMailShare(token, threadId, searchId, uuidv4(), email)
      if(res.responsecode == 200){
        setMessage(`Shared with ${email}`);
        setTimeout(() => setMessage(''), 7000);
        setEmail("");
      } else {
        setErrorMessage(" Failed to send email");
      }
    } catch (err) {
      setErrorMessage("Failed to send email");
    }
    setIsLoading(false);
  };

  const openWhatsApp = () => {
    if (!shareLink) return;
    const text = encodeURIComponent(`Check out this workflow: ${shareLink}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const openLinkedIn = () => {
    if (!shareLink) return;
    const url = encodeURIComponent(shareLink);
    const text = encodeURIComponent("Check out this workflow I found on Tuly.app!");
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${text}`, "_blank");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white shadow-xl w-full max-w-md p-6 relative">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black">
          <X size={20} />
        </button>

        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Share2 className="text-blue-600" /> Share Workflow
        </h2>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          <button
            className={`pb-2 ${activeTab === "email" ? "border-b-2 border-blue-600 font-medium" : "text-gray-500"}`}
            onClick={() => setActiveTab("email")}>
            Invite via Email
          </button>

          <button
            className={`pb-2 ${activeTab === "link" ? "border-b-2 border-blue-600 font-medium" : "text-gray-500"}`}
            onClick={() => setActiveTab("link")}>
            Generate Link
          </button>

        </div>

        {/* Invite via Email */}
        {activeTab === "email" && (
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter recipient email"
              className="w-full px-3 py-2 border rounded-lg mb-3"
            />
            <button
              onClick={handleSendEmail}
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition flex items-center gap-2">
              <Mail size={18} />
              {isLoading ? "Sending..." : "Send Invite"}
            </button>
          </div>
        )}

        {/* Generate Link */}
        {activeTab === "link" && (
          <div>
            {!shareLink && (
              <button
                onClick={handleGenerateLink}
                disabled={isLoading}
                className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition">
                {isLoading ? "Generating..." : "Get Share Link"}
              </button> 
            )}

            {shareLink && (
              <>
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-sm"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(shareLink)}
                  className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                >
                  <Copy size={18} />
                </button>
              </div>
              <br/>
              <div className="flex flex-col gap-4">
                
                <button
                  onClick={openWhatsApp}
                  disabled={!shareLink}
                  className="flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                >
                  <MessageCircle size={18} /> Share on WhatsApp
                </button>
                <button
                  onClick={openLinkedIn}
                  disabled={!shareLink}
                  className="flex items-center justify-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition"
                >
                  <Linkedin size={18} /> Share on LinkedIn
                </button>
              </div>
              </>
            )}
          </div>
        )}

        {/* Status Message */}
        {message && (
          <div>
            <br />
            <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {message}
            </div>
          </div>
        )}
        {errorMessage && (
          <div>
            <br />
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errorMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
